<script setup>
import AppCard from "@/components/AppCard";
import AppRow from "@/components/AppRow";
import AppButton from "@/components/AppButton";
import AppFormInput from "@/components/Form/AppFormInput";
import AppFileUpload from "@/components/Form/AppFileUpload";
import AppFileUploadOCR from "@/components/Form/AppFileUploadOCR";
import AppCheckbox from "@/components/Form/AppCheckbox";
// import getFormFields from "@/utils/getFormFields";

import { onMounted, ref, computed, defineProps, defineEmits } from "vue";
import { store } from "@/store";
import { useRoute } from "vue-router";

// Api
import useApi from "@/composables/useApi";
const { user, contract } = useApi();

const route = useRoute();

const props = defineProps({
    state: {
        type: Object,
        default: () => ({})
    }
});

// Use uploader with OCR
const ocrEnabled = computed(() => {
    const envValue = process.env.VUE_APP_OCR_ENABLED;
    // Accepts 'true' (string) or boolean true as enabled, otherwise false
    return envValue === 'true' || envValue === true;
});
console.log("ocrEnabled", ocrEnabled.value);

const emit = defineEmits(["next", "previous", "click", "end"]);

const loading = computed(() => store.APP_LOADING);
const sales_catalog_id = computed(() => store.sales_catalog_id);
const checkvalue = ref(false);
const checkValueConfirm = ref(false);
const checkValueLocation = ref(false);

const catalog_uso_cfdi = ref([]);
const catalog_regimen_fiscal = ref([]);
const catalog_ciudad_cobertura = ref([]);
const catalog_asentamiento = ref([]);
const catalog_buildings = ref([]);
//
const uso_cfdi = ref(null);
const regimen_fiscal = ref(null);
const ciudad_cobertura_id = ref(2);
const asentamiento_id = ref(null);
const build_id = ref(null);
const account_setting_id = ref(null);

const currency_id = 33;

// Parsed data from OCR
const parsed_identificacion_frontal = ref(null);
const parsed_identificacion_posterior = ref(null);
const parsed_comprobante_domicilio = ref(null);
const parsed_constancia_situacion_fiscal = ref(null);


const id_vendedor = computed(() => {
    return route?.query?.id;
});

const get_catalog_uso_cfdi = async () => {
    try {
        catalog_uso_cfdi.value = await contract.getUsoCFDI();
    } catch (error) {
        console.log(error);
    }
}

const get_catalog_regimen_fiscal = async () => {
    try {
        catalog_regimen_fiscal.value = await contract.getRegimenFiscal();
    } catch (error) {
        console.log(error);
    }
}

const get_account_setting = async () => {
    try {
        const team_id = 1;
        const data = await contract.get_account_setting(currency_id, team_id);
        account_setting_id.value = data.payload[0].customer_account_id[0];
        // console.log(data);
    } catch (error) {
        console.log(error);
    }
}

const regexCURP = /^[A-Z]{1}[AEIOU]{1}[A-Z]{2}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[HM]{1}(AS|BC|BS|CC|CL|CM|CS|DF|DG|GT|GR|HG|JC|MC|MN|MS|NT|NL|OC|PL|QT|QR|SP|SL|SR|TC|TS|TL|VZ|YN|ZS|NE)[B-DF-HJ-NP-TV-Z]{3}[0-9A-Z]{1}\d{1}$/;
const validarCURP = (curp) => regexCURP.test((curp || "").toUpperCase());

const regexRFC = /^[A-ZÑ&]{3,4}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[A-Z0-9]{2}[0-9A]$/;
const validarRFC = (rfc) => regexRFC.test((rfc || "").toUpperCase());

const curpScanner = /[A-Z][AEIOU][A-Z]{2}\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|BC|BS|CC|CL|CM|CS|DF|DG|GT|GR|HG|JC|MC|MN|MS|NT|NL|OC|PL|QT|QR|SP|SL|SR|TC|TS|TL|VZ|YN|ZS|NE)[B-DF-HJ-NP-TV-Z]{3}[0-9A-Z]\d/g;

const ID_LABELS = [
    "NOMBRE",
    "DOMICILIO",
    "CURP",
    "CLAVE DE ELECTOR",
    "SEXO",
    "FECHA DE NACIMIENTO",
    "AÑO DE REGISTRO",
    "SECCIÓN",
    "SECCION",
    "VIGENCIA",
    "ESTADO",
    "MUNICIPIO",
    "LOCALIDAD",
    "EMISIÓN",
    "EMISION",
    "FOLIO",
];

const cleanText = (s) =>
    (s || "")
        .replace(/\n+/g, " ")
        .replace(/\s+/g, " ")
        .trim();

const isOnlyLabel = (txt) => {
    const t = cleanText(txt).toUpperCase();
    return ID_LABELS.some((l) => t === l);
};

// Identificacion frontal

const onIDOcrSuccess = (data) => {
    const layoutItems = data?.layout_details?.[0] || [];
    const items = layoutItems
        .filter((it) => typeof it?.content === "string" && it.content.trim())
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));

    // NOMBRE: buscar el item cuyo contenido inicia con "NOMBRE"
    let nombre = "";
    const nombreItem = items.find((it) =>
        cleanText(it.content).toUpperCase().startsWith("NOMBRE")
    );
    if (nombreItem) {
        const txt = cleanText(nombreItem.content).replace(/^NOMBRE\s*/i, "").trim();
        if (txt) {
            nombre = txt;
        } else {
            // El item contiene solo la etiqueta "NOMBRE": tomar siguientes items
            // hasta encontrar otra etiqueta conocida.
            const startIdx = items.indexOf(nombreItem);
            const parts = [];
            for (let i = startIdx + 1; i < items.length; i++) {
                const t = cleanText(items[i].content);
                if (!t) continue;
                if (isOnlyLabel(t)) break;
                parts.push(t);
                if (parts.length >= 3) break;
            }
            nombre = parts.join(" ");
        }
    }

    // DOMICILIO: buscar item que inicie con "DOMICILIO"
    let direccion = "";
    const domicilioItem = items.find((it) =>
        cleanText(it.content).toUpperCase().startsWith("DOMICILIO")
    );
    if (domicilioItem) {
        const txt = cleanText(domicilioItem.content).replace(/^DOMICILIO\s*/i, "").trim();
        if (txt) {
            direccion = txt;
        } else {
            const startIdx = items.indexOf(domicilioItem);
            const parts = [];
            for (let i = startIdx + 1; i < items.length; i++) {
                const t = cleanText(items[i].content);
                if (!t) continue;
                if (isOnlyLabel(t)) break;
                if (curpScanner.test(t.replace(/\s/g, ""))) break;
                curpScanner.lastIndex = 0; // reset estado del regex global
                parts.push(t);
                if (parts.length >= 3) break;
            }
            direccion = parts.join(" ");
        }
    }

    // CURP: escanear todos los items y validar con la expresión regular
    let curp = "";
    for (const it of items) {
        const upper = cleanText(it.content).toUpperCase();
        const tokens = upper.split(/\s+/);
        const tokenMatch = tokens.find((tok) => validarCURP(tok));
        if (tokenMatch) {
            curp = tokenMatch;
            break;
        }
        curpScanner.lastIndex = 0;
        const scan = upper.match(curpScanner);
        if (scan && scan.length) {
            const candidate = scan.find((c) => validarCURP(c));
            if (candidate) {
                curp = candidate;
                break;
            }
        }
    }

    const content = data?.md_results || "";

    const parsed = { nombre, direccion, curp, content };
    console.log("[AppFileUploadOCR] ocr-success parsed:", parsed);
    parsed_identificacion_frontal.value = parsed;

    // Si el input "name" está vacío, autocompletarlo con el nombre extraído del OCR
    if (nombre) {
        const nameInput = document.querySelector('input[name="name"]');
        if (nameInput && nameInput.value === "") {
            nameInput.value = nombre;
            nameInput.dispatchEvent(new Event("input", { bubbles: true }));
            nameInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
    }
};

const onIDOcrError = (error) => {
    console.log("[AppFileUploadOCR] ocr-error:", error);
};

const onIDOcrProgress = (progress) => {
    console.log("[AppFileUploadOCR] ocr-progress:", progress);
};

// Identificacion posterior

const onIDPosteriorOcrSuccess = (data) => {
    console.log("[AppFileUploadOCR] ocr-success:", data);
    const content = data?.md_results || "";
    const parsed = { content };
    console.log("[AppFileUploadOCR] ocr-success parsed:", parsed);
    parsed_identificacion_posterior.value = parsed;
};

const onIDPosteriorOcrError = (error) => {
    console.log("[AppFileUploadOCR] ocr-error:", error);
};

const onIDPosteriorOcrProgress = (progress) => {
    console.log("[AppFileUploadOCR] ocr-progress:", progress);
};

// Comprobante de domicilio

const onComprobanteDomicilioOcrSuccess = (data) => {
    console.log("[AppFileUploadOCR] ocr-success:", data);
    const content = data?.md_results || "";
    const parsed = { content };
    console.log("[AppFileUploadOCR] ocr-success parsed:", parsed);
    parsed_comprobante_domicilio.value = parsed;
};

const onComprobanteDomicilioOcrError = (error) => {
    console.log("[AppFileUploadOCR] ocr-error:", error);
};

const onComprobanteDomicilioOcrProgress = (progress) => {
    console.log("[AppFileUploadOCR] ocr-progress:", progress);
};

// Constancia de situación fiscal

const onConstanciaSituacionFiscalOcrSuccess = (data) => {
    console.log("[AppFileUploadOCR] ocr-success:", data);
    const content = data?.md_results || "";

    // Aplanar items de todas las páginas con contenido textual
    const allItems = (data?.layout_details || [])
        .flat()
        .filter((it) => typeof it?.content === "string" && it.content.trim());

    const allText = [content, ...allItems.map((it) => it.content)]
        .join(" ")
        .toUpperCase();

    // VAT (RFC): tokenizar y validar contra la expresión regular
    let vat = "";
    const tokens = allText.split(/[^A-ZÑ&0-9]+/);
    for (const tok of tokens) {
        if (validarRFC(tok)) {
            vat = tok;
            break;
        }
    }

    // ZIP: capturar 5 dígitos posteriores a "Código Postal"
    let zip = "";
    const zipMatch = allText.match(/C[ÓO]DIGO\s*POSTAL\s*:?\s*(\d{5})/);
    if (zipMatch) zip = zipMatch[1];

    // Types (Régimenes): localizar la tabla con cabecera Régimen / Fecha Inicio / Fecha Fin
    const types = [];
    const tablesItems = allItems.filter((it) =>
        /^<table/i.test((it.content || "").trim())
    );
    const regimenTable = tablesItems.find((it) => {
        const c = it.content;
        return (
            /<td>\s*R[ÉE]GIMEN\s*<\/td>/i.test(c) &&
            /<td>\s*FECHA\s+INICIO\s*<\/td>/i.test(c) &&
            /<td>\s*FECHA\s+FIN\s*<\/td>/i.test(c)
        );
    });
    if (regimenTable) {
        const rows = regimenTable.content.match(/<tr>[\s\S]*?<\/tr>/gi) || [];
        // saltar la primera fila (cabecera)
        for (let i = 1; i < rows.length; i++) {
            const cellMatch = rows[i].match(/<td>([\s\S]*?)<\/td>/i);
            if (cellMatch) {
                const text = cellMatch[1].replace(/<[^>]+>/g, "").trim();
                if (text) types.push(text);
            }
        }
    }

    // RAZÓN SOCIAL:
    // Caso Persona Física: concatenar "Nombre (s)" + "Primer Apellido" + "Segundo Apellido".
    // Caso Persona Moral: usar el bloque previo a la etiqueta
    // "Nombre, denominación o razón social".
    let razon_social = "";
    let nombres = "";
    let primer_apellido = "";
    let segundo_apellido = "";

    const getValueForLabel = (idx, regex) => {
        const t = cleanText(allItems[idx].content);
        const m = t.match(regex);
        if (!m) return null;
        const inline = (m[1] || "").trim();
        if (inline) return inline;
        // Etiqueta sola: tomar el siguiente item con contenido
        for (let j = idx + 1; j < allItems.length; j++) {
            const nt = cleanText(allItems[j].content);
            if (nt) return nt;
        }
        return "";
    };

    for (let i = 0; i < allItems.length; i++) {
        if (!nombres) {
            const v = getValueForLabel(i, /^Nombre\s*\(s\)\s*:?\s*(.*)$/i);
            if (v !== null) { nombres = v; continue; }
        }
        if (!primer_apellido) {
            const v = getValueForLabel(i, /^Primer\s+Apellido\s*:?\s*(.*)$/i);
            if (v !== null) { primer_apellido = v; continue; }
        }
        if (!segundo_apellido) {
            const v = getValueForLabel(i, /^Segundo\s+Apellido\s*:?\s*(.*)$/i);
            if (v !== null) { segundo_apellido = v; continue; }
        }
    }

    if (nombres || primer_apellido || segundo_apellido) {
        razon_social = [nombres, primer_apellido, segundo_apellido]
            .filter(Boolean)
            .join(" ")
            .replace(/\s+/g, " ")
            .trim();
    } else {
        // Persona Moral: extraer las líneas previas a "Nombre, denominación o razón social"
        const itemRazon = allItems.find((it) =>
            /denominaci[óo]n\s+o\s+raz[óo]n\s+social/i.test(
                (it.content || "").replace(/\s+/g, " ")
            )
        );
        if (itemRazon) {
            const lines = (itemRazon.content || "")
                .split(/\n+/)
                .map((s) => s.trim())
                .filter(Boolean);
            const labelIdx = lines.findIndex((l) =>
                /denominaci[óo]n\s+o\s+raz[óo]n/i.test(l)
            );
            if (labelIdx > 0) {
                razon_social = lines.slice(0, labelIdx).join(" ").trim();
            }
        }
    }

    const parsed = { vat, zip, razon_social, types, content };
    console.log("[AppFileUploadOCR] ocr-success parsed:", parsed);
    parsed_constancia_situacion_fiscal.value = parsed;

    // Autocompletar inputs del formulario sólo cuando estén vacíos.
    const fillInputIfEmpty = (selector, value) => {
        if (!value) return;
        const input = document.querySelector(selector);
        if (input && input.value === "") {
            input.value = value;
            input.dispatchEvent(new Event("input", { bubbles: true }));
            input.dispatchEvent(new Event("change", { bubbles: true }));
        }
    };

    fillInputIfEmpty('input[name="razon_social"]', razon_social);
    fillInputIfEmpty('input[name="vat"]', vat);
    fillInputIfEmpty('input[name="zip"]', zip);
};

const onConstanciaSituacionFiscalOcrError = (error) => {
    console.log("[AppFileUploadOCR] ocr-error:", error);
};

const onConstanciaSituacionFiscalOcrProgress = (progress) => {
    console.log("[AppFileUploadOCR] ocr-progress:", progress);
};

const backStep = async () => {
    console.log("backStep...");
    emit("previous");
};

const nextStep = async () => {
    console.log("nextStep...");
    emit("next");
};

onMounted(async () => {
    try {
        store.APP_LOADING = true;
        await get_catalog_uso_cfdi();
        await get_catalog_regimen_fiscal();
        // await get_catalog_ciudad_cobertura();
        // await get_catalog_asentamiento();
        // await get_catalog_buildings();
        await get_account_setting();
    } catch (error) {
        console.log(error);
    }finally{
        store.APP_LOADING = false;
    }
});

const getFormFieldsCustom = async (e) => {
    e.preventDefault();
    const formElement = e.target;
    const formData = new FormData(formElement);
    const formObject = Object.fromEntries(formData.entries());
    const fields = formObject;

    // Get all checkboxes from the form
    const checkboxes = formElement.querySelectorAll('input[type="checkbox"]');
    
    // Add checkbox values to fields object
    checkboxes.forEach((checkbox) => {
        fields[checkbox.name] = checkbox.checked;
    });

    // Get all file inputs and convert to base64
    const fileInputs = formElement.querySelectorAll('input[type="file"]');
    
    for (const fileInput of fileInputs) {
        if (fileInput.files[0]) {
            const file = fileInput.files[0];
            const base64 = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(file);
            });
            const string_base64 = base64.split(',')[1];
            fields[fileInput.name] = string_base64;
        } else {
            fields[fileInput.name] = null;
        }
    }

    return fields;
}

const onSubmit = async (e) => {
    try {
        console.log("onSubmit...");
        const fields = await getFormFieldsCustom(e);
        console.log(fields);
        // Validar campos obligatorios
        if (!fields.name || !fields.email || !fields.mobile || !fields.calle_servicio || !fields.cp_servicio || !fields.description) {
            return window.alert("Los campos obligatorios son: Nombre del cliente, Email, Teléfono, Dirección de servicio, CP de servicio e Información adicional");
        }
        // Validar campos adjuntos obligatorios
        if (!fields.identificacion_file || !fields.comprobante_domicilio_file) {
            return window.alert("Los campos adjuntos obligatorios son: Identificación y Comprobante de domicilio");
        }
        if (checkvalue.value && (!regimen_fiscal.value || !uso_cfdi.value)) {
            return window.alert("Los campos obligatorios para facturación son: Régimen Fiscal y Uso CFDI");
        }
        if (!fields.constancia_situacion_fiscal_file && checkvalue.value) {
            return window.alert("El campo adjunto obligatorio es: Constancia de situación fiscal si deseas facturar");
        }
        store.APP_LOADING = true;
        // Creando el partner
        const fields_partner = {
            name: fields.name,
            email: fields.email,
            mobile: fields.mobile,
            phone: fields.mobile,
            cliente: true,
            razon_social: checkvalue.value ? fields.razon_social : fields.name,
            vat: checkvalue.value ? fields.vat : "XAXX010101000",
            zip: checkvalue.value ? fields.zip : "77500",
            street_name: checkvalue.value ? fields.street_name : "Indefinido",
            street_number: checkvalue.value ? fields.street_number : "Indefinido",
            street_number2: checkvalue.value ? fields.street_number2 : "Indefinido",
            l10n_mx_edi_fiscal_regime: checkvalue.value ? fields.l10n_mx_edi_fiscal_regime : "616",
            l10n_mx_edi_usage: checkvalue.value ? fields.l10n_mx_edi_usage : "S01",
            l10n_mx_edi_curp: parsed_identificacion_frontal.value?.curp || "",
        }
        

        console.log(fields_partner);

        const data_partner = await contract.create_partner(fields_partner);
        console.log("--------------------------------");
        console.log(data_partner);
        console.log("--------------------------------");

        const partner_id = data_partner.payload[0];

        console.log(partner_id);
        //
        const partner_messages = [
            {
                title: "Identificación (frontal)",
                content: parsed_identificacion_frontal.value?.content,
            },
            {
                title: "Identificación (posterior)",
                content: parsed_identificacion_posterior.value?.content,
            },
            {
                title: "Comprobante de domicilio",
                content: parsed_comprobante_domicilio.value?.content,
            },
            {
                title: "Constancia de situación fiscal",
                content: parsed_constancia_situacion_fiscal.value?.content,
            },
        ];

        for (const msg of partner_messages) {
            if (typeof msg.content !== "string" || msg.content.trim() === "") continue;
            const body = `<b>${msg.title}</b><br/>${msg.content}`;
            const send_partner_message = await user.sendMailMessage(
                "res.partner",
                partner_id,
                "notification",
                body
            );
            console.log("--------------------------------");
            console.log(`sendMailMessage [${msg.title}]`, send_partner_message);
            console.log("--------------------------------");
        }

        // Creando el lead
        const fields_lead = {
            name: fields.name,
            partner_id: partner_id,
            team_id: 1,
            phone: fields.mobile,
            user_id: 2,
            asesor_preventa: fields.asesor_preventa,
            // ciudad_cobertura_id: !checkValueLocation.value ? ciudad_cobertura_id.value : null,
            latitud_contact: store.map_location.lat,
            longitud_contact: store.map_location.lng,
            // asentamiento_id: !checkValueLocation.value ? asentamiento_id.value : null,
            // build_id: !checkValueLocation.value ? build_id.value : null,
            calle_servicio: fields.calle_servicio,
            num_ext_servicio: fields.num_ext_servicio,
            num_int_servicio: fields.num_int_servicio,
            referencia_ubicacion_servicio: store.map_location.address.formatted,
            coordenadas_contact: `https://maps.google.com/?q=${store.map_location.lat.toString()},${store.map_location.lng.toString()}`,
            cp_servicio: fields.cp_servicio,
            description: `${fields.description} <br/> Dirección: ${store.map_location.address.formatted} <br/> CP: ${store.map_location.address.zip}`,
            identificacion_file: fields.identificacion_file,
            identificacion_file_posterior: fields.identificacion_file_posterior ? fields.identificacion_file_posterior : false,
            constancia_situacion_fiscal_file: fields.constancia_situacion_fiscal_file ? fields.constancia_situacion_fiscal_file : false,
            comprobante_domicilio_file: fields.comprobante_domicilio_file,
        }

        console.log(fields_lead);

        const data_lead = await contract.create_lead(fields_lead);
        console.log("--------------------------------");
        console.log(data_lead);
        console.log("--------------------------------");

        const lead_id = data_lead.payload[0];
        console.log(lead_id);

        // Enviar mensaje al lead
        const send_lead_message = await user.sendMailMessage(
            "crm.lead",
            lead_id,
            "notification",
            parsed_comprobante_domicilio.value?.content || ""
        );
        console.log("--------------------------------");
        console.log(send_lead_message);
        console.log("--------------------------------");

        // Convertir el lead a oportunidad
        const data_lead_opportunity = await contract.update_lead(lead_id, {type: "opportunity", stage_id: 1});

        console.log("--------------------------------");
        console.log(data_lead_opportunity);
        console.log("--------------------------------");

        // Crear la orden de venta del plan de internet
        const fields_sale_order = {
            opportunity_id: lead_id,
            partner_id: partner_id,
            sales_catalog_id: sales_catalog_id.value.sales_catalog_id,
            is_autorizado: true,
            state: "draft",
            duration_contract: parseInt(sales_catalog_id.value.plazo_minimo_meses),
            customer_account_id: account_setting_id.value
        }
        // Crear la orden de venta del plan de internet
        console.log("Creando la orden de venta del plan de internet");
        const data_sale_order = await contract.create_sale_order(fields_sale_order);

        const sale_order_id = data_sale_order.payload[0];
        console.log(sale_order_id);

        // Crear la linea de la orden de venta del plan de internet
        const fields_sale_order_line = {
            order_id: sale_order_id,
            name: `${sales_catalog_id.value.nombre_comercial} - ${sales_catalog_id.value.product_template_name}`,
            product_id: sales_catalog_id.value.product_id,
            product_uom_qty: 1,
            product_uom: 21,
            price_unit: sales_catalog_id.value.renta_mensual_sin_iva,
        }
        const data_sale_order_line = await contract.create_sale_order_line(fields_sale_order_line);
        console.log(data_sale_order_line);

        const sale_order_line_id = data_sale_order_line.payload[0];
        console.log(sale_order_line_id);

        // Confirmar la orden de venta
        const data_confirm_sale_order = await contract.confirm_sale_order(sale_order_id);
        console.log(data_confirm_sale_order);
        if (!sales_catalog_id.value.sin_costo_instalacion) {
            console.log("Generando orden de venta de contratacion y/o instalacion");
            // Crear la orden de venta de contratacion y/o instalacion
            const data_sale_order_contracting = await contract.generate_sales_order_for_contracting(lead_id, currency_id);
            console.log(data_sale_order_contracting);
        }
        else {
            console.log("Sin costo de instalacion");
        }
        // Convertir la oportunidad a ganada
        const data_won_lead = await contract.update_lead(lead_id, {"stage_id": 4});
        console.log(data_won_lead);

        // Guardar los datos en el store
        store.partner_id = partner_id;
        store.lead_id = lead_id;
        store.sale_order_id = sale_order_id;
        store.sale_order_line_id = sale_order_line_id;

        // Guardar los datos en el localStorage
        localStorage.setItem("lead_id", lead_id);
        localStorage.setItem("sale_order_id", sale_order_id);

        emit("next");
    } catch (error) {
        console.error("Error in onSubmit:", error);
        throw error;
    }finally{
        store.APP_LOADING = false;
    }
};

</script>
<template lang="pug">
//- pre {{ state.plan }}
//- pre {{ account_setting_id }}
//- pre {{ sales_catalog_id }}
//- pre {{ catalog_uso_cfdi }}
//- pre {{ catalog_regimen_fiscal }}
//- pre {{ catalog_ciudad_cobertura }}
//- pre {{ catalog_asentamiento }}
//- pre {{ catalog_buildings }}
form(@submit.prevent="onSubmit")
    AppCard
        h2.mt-2 Plan de internet seleccionado
        .row
            div(style="width: 100%;")
                .row.mb-2
                    .card-plan(style="background-color: #2a5aad; border-radius: 8px; padding: 1.5rem; width: 100%;")
                        .plan-details(style="color: white; display: flex; justify-content: space-between; align-items: center; gap: 1rem;")
                            .plan-name(style="font-size: 1.5rem; font-weight: bold;") {{ sales_catalog_id.nombre_comercial }} |
                            .plan-speed(style="display: flex; align-items: center; gap: 0.5rem;")
                                span(style="font-size: 1.25rem;") Velocidad:
                                span(style="font-size: 2rem; font-weight: bold;") {{ sales_catalog_id.velocidad_incluida }}
                                span(style="font-size: 1.25rem;") Mbps
                            .plan-price(style="display: flex; align-items: center; gap: 0.25rem;")
                                span(style="font-size: 1.25rem;") $
                                span(style="font-size: 2rem; font-weight: bold;") {{ sales_catalog_id.renta_mensual_con_iva }}
                                span(style="font-size: 1.25rem;") MXN
                            .plan-price(style="display: flex; align-items: center; gap: 0.25rem;")
                                span(style="font-size: 1.25rem;") MENSUAL
        h1 {{$t('contract.contact_form.form_name')}}
        .row
            AppFormInput(type="text" :label="$t('contract.contact_form.name')" name="name" required)
            AppFormInput(type="text" :label="$t('contract.contact_form.id_vendedor')" name="asesor_preventa" :value="id_vendedor" :readonly="!!id_vendedor")
        .row
            AppFormInput(type="email" :label="$t('contract.contact_form.email')" name="email" required)
            .row
                AppFormInput(type="text" :label="$t('contract.contact_form.mobile')" name="mobile" required)
        .row(v-if="ocrEnabled")
            AppFileUploadOCR.mb-4(
                icon="user-square"
                name="identificacion_file"
                :label="$t('contract.contact_form.identificacion')"
                @ocr-success="onIDOcrSuccess"
                @ocr-error="onIDOcrError"
                @ocr-progress="onIDOcrProgress"
            )
            AppFileUploadOCR.mb-4(
                icon="user-square"
                name="identificacion_file_posterior"
                :label="$t('contract.contact_form.identificacion_posterior')"
                @ocr-success="onIDPosteriorOcrSuccess"
                @ocr-error="onIDPosteriorOcrError"
                @ocr-progress="onIDPosteriorOcrProgress"
            )
            AppFileUploadOCR.mb-4(
                icon="user-square"
                name="comprobante_domicilio_file"
                :label="$t('contract.contact_form.comprobante')"
                @ocr-success="onComprobanteDomicilioOcrSuccess"
                @ocr-error="onComprobanteDomicilioOcrError"
                @ocr-progress="onComprobanteDomicilioOcrProgress"
            )
        .row(v-if="!ocrEnabled")
            AppFileUpload.mb-4(
            icon="user-square"
            name="identificacion_file"
            :label="$t('contract.contact_form.identificacion')"
            )
            AppFileUpload.mb-4(
            icon="user-square"
            name="identificacion_file_posterior"
            :label="$t('contract.contact_form.identificacion_posterior')"
            )
            AppFileUpload.mb-4(
            icon="map"
            name="comprobante_domicilio_file"
            :label="$t('contract.contact_form.comprobante')"
            )
        h1.mt-2 Datos fiscales
        h2.mt-2 Si deseas facturar, por favor ingresa los siguientes datos
        .row.mt-4
            AppCheckbox(:label="$t('contract.contact_form.invoice_check')"  v-model="checkvalue")
        div(v-show="checkvalue")
            .row
                small.text-muted.mt-2 {{$t('contract.contact_form.fiscal_data')}}
            .row.mt-4(v-if="ocrEnabled")
                AppFileUploadOCR.mb-4(
                    icon="map"
                    name="constancia_situacion_fiscal_file"
                    :label="$t('contract.contact_form.constancia_situacion_fiscal')"
                    @ocr-success="onConstanciaSituacionFiscalOcrSuccess"
                    @ocr-error="onConstanciaSituacionFiscalOcrError"
                    @ocr-progress="onConstanciaSituacionFiscalOcrProgress"
                )
            .row(v-if="!ocrEnabled")
                AppFileUpload.mb-4(
                icon="map"
                name="constancia_situacion_fiscal_file"
                :label="$t('contract.contact_form.constancia_situacion_fiscal')"
                )
            .row
                AppFormInput(type="text" :label="$t('contract.contact_form.business_name')" name="razon_social" :required="checkvalue")
                AppFormInput(type="text" :label="$t('contract.contact_form.identification')" name="vat" :required="checkvalue" pattern="[A-ZÑ&amp;]{3,4}[0-9]{2}[0-1][0-9][0-3][0-9][A-Z0-9]?[A-Z0-9]?[0-9A-Z]?")
                AppFormInput(type="number" :label="$t('contract.address_form.zip_code')" name="zip" :required="checkvalue")
            .row
                div
                    span(class="label") Regimen Fiscal
                    select(v-model="regimen_fiscal" class="input" name="l10n_mx_edi_fiscal_regime")
                        option(:value="item.value" v-for="item in catalog_regimen_fiscal" :key="item.value")
                            | {{item.label}}
                div
                    span(class="label") Uso CFDI
                    select(v-model="uso_cfdi" class="input" name="l10n_mx_edi_usage")
                        option(:value="item.value" v-for="item in catalog_uso_cfdi" :key="item.value")
                            | {{item.label}}
        h1.mt-2 {{$t('contract.address_form.form_name')}}
        .row
            div
        h2.mt-2 Detalles de la ubicación
        .row
            .row
                AppFormInput(type="text" :label="$t('contract.address_form.street')" name="calle_servicio" required)
                AppFormInput(type="number" :label="$t('contract.address_form.zip_code')" name="cp_servicio" :value="store.map_location.address.zip" required)
        AppFormInput(type="textarea" :label="$t('contract.address_form.aditional_info')" name="description" required)
        AppCheckbox(:label="$t('contract.contact_form.check')" v-model="checkValueConfirm")
        AppRow.mt-5.btns-steps
            //- button(@click="backStep") Back
            AppButton.mb-0.bg-gignetgreen-500(:label="$t('wizard_contract.address.btn_back')" @click="backStep")
            AppButton.mb-0.bg-gignetgreen-500(:label="$t('wizard_contract.address.btn_send')" :loading="loading" :disabled="loading || !checkValueConfirm")
</template>
<style lang="scss" scoped>
h1 {
  @apply text-bluegray-500;
  @apply font-semibold;
  @apply text-xl;
  @apply mb-3;
}
h2 {
  @apply text-bluegray-400;
  @apply font-semibold;
  @apply text-base;
  @apply mb-3;
}
.row {
  @screen md {
    @apply grid;
    @apply gap-4;
    @apply grid-flow-col;
    @apply auto-cols-fr;
  }
  &.margin {
    @apply mt-2;
    @apply mb-5;
  }
}

.bg-gignetgreen-500 {
  background-color: rgba(21, 199, 208, var(--tw-bg-opacity));
}
.bg-gignetgreen-500:hover {
  background-color: rgba(18, 172, 180, var(--tw-bg-opacity));
}
.box-address {
  margin-top: 10px;
  margin-bottom: 10px;
}

.margin-badge {
  margin-left: 2px;
  margin-right: 2px;
}

.btns-steps {
  margin-top: 30px;
}

.form-input {
  @apply inline-block;
  @apply w-full;
  &__required {
    @apply text-red-400;
  }
}

.input {
  @apply mb-3;
  @apply block;
  @apply w-full;
  @apply rounded-md;
  @apply border-gray-300;
  @apply shadow-sm;
  @apply px-3;
  @apply py-2;
  @apply text-bluegray-700;
  @apply text-sm;
  @apply transition-all;
  @apply outline-none;
  &:focus:invalid {
    @apply border-red-300;
    @apply shadow-red-300-md;
  }
  &:focus {
    @apply border-gignetblue-300;
    @apply ring;
    @apply ring-gignetblue-200;
    @apply ring-opacity-50;
    @apply ring-offset-2;
  }

  &.disabled {
    @apply bg-bluegray-50;
    @apply text-bluegray-400;
  }
}

.label {
  @apply text-bluegray-400;
  @apply uppercase;
  @apply text-sm;
  @apply font-medium;
  @apply mb-2;
  @apply inline-block;
  @apply tracking-wide;
}
</style>